import React, { useEffect } from 'react';
import PageWrapper from './PageWrapper';
import EditableText from './EditableText';
import { RadarChart } from '../../Feedback360ResultsPage/children/Circumplex/children/radarChart';
import { DonutChart } from '../../Feedback360ResultsPage/children/Circumplex/children/donutChart';
import { FEEDBACK_360_SURVEY } from '../../../../config/questionTypeConstants';
import { generateTints } from '../../SurveyWizard/children/ReportSetup/children/CircumplexConfig/helper';

const buildRadarColorPalette = (donutChartData = []) => {
  if (donutChartData.some((pillar) => !pillar.color)) return undefined;
  return donutChartData.reduce((palette, pillar) => {
    const count = Math.round(Number(pillar.numberOfSubCategory)) || 0;
    return palette.concat(generateTints(pillar.color, count));
  }, []);
};

// A single circumplex (radar + donut) rendered at the shared PDF size.
// `idKey` must be unique per instance on the page: the radar and donut charts
// mount amCharts into a fixed DOM id (defaulting to "chartdiv"/"donutChartDiv"),
// so rendering two circumplexes with the same ids makes them collide and one
// paints blank. A distinct idKey gives each its own mount element.
function Circumplex({ chartContent, chartFontSizes = {}, idKey = 'main' }) {
  const { subCategoryFontSize, axisFontSize, categoryFontSize } = chartFontSizes;
  return (
    <div className="pdf-circumplex-container" data-circumplex-target>
      <RadarChart
        isForPdfGeneration={true}
        chartId={`chartdiv-${idKey}`}
        radarChartData={chartContent.radarChartData}
        subCategoryFontSize={subCategoryFontSize}
        axisFontSize={axisFontSize}
        logoUrl={chartContent.logoUrl}
        responsiveLogo={!!chartContent.logoUrl}
        projectType={FEEDBACK_360_SURVEY.MODULAR_360_FEEDBACK}
        colorPalette={buildRadarColorPalette(chartContent.donutChartData)}
      />
      <DonutChart
        chartId={`donutChartDiv-${idKey}`}
        donutData={chartContent.donutChartData}
        categoryFontSize={categoryFontSize}
        projectType={FEEDBACK_360_SURVEY.MODULAR_360_FEEDBACK}
      />
    </div>
  );
}

function ChartLegend() {
  return (
    <div className="pdf-chart-legend">
      <div className="pdf-chart-legend__item">
        <span className="pdf-chart-legend__color" style={{ backgroundColor: '#2E74B5', opacity: 0.3, border: '2px solid #2E74B5' }}></span>
        <span>All But Self scores</span>
      </div>
      <div className="pdf-chart-legend__item">
        <span className="pdf-chart-legend__line" style={{ borderTop: '2px solid #333' }}></span>
        <span>Self scores on the 6-pt scale</span>
      </div>
      <div className="pdf-chart-legend__item">
        <span className="pdf-chart-legend__line" style={{ borderTop: '2px dashed #999' }}></span>
        <span>Norm</span>
      </div>
    </div>
  );
}

function SummaryIntro() {
  return (
    <>
      <h2 className="pdf-section-heading pdf-section-heading--numbered">
        <span className="pdf-section-heading__badge">1</span>
        <EditableText
          as="span"
          path="circumplexData.sectionHeading"
          variant="inline"
          stripInlineWrap
        />
      </h2>
      <EditableText
        as="p"
        className="pdf-body-text"
        path="circumplexData.intro"
        variant="block"
        stripInlineWrap
      />
      <EditableText
        as="p"
        className="pdf-body-text"
        path="circumplexData.interpretLead"
        variant="inline"
        stripInlineWrap
      />
      <EditableText
        as="div"
        className="pdf-bullet-list"
        path="circumplexData.interpretBullets"
        variant="block"
      />
    </>
  );
}

export default function ResultsSummaryPage({ data, personName, chartFontSizes = {}, pageNumber, reportType = 'standard' }) {
  useEffect(() => {
    // The chart is rendered with appear(0) for PDF generation (no animation),
    // so it paints almost immediately. Wait two animation frames to guarantee
    // the chart canvas has actually committed to the DOM, then signal Puppeteer.
    let raf1, raf2, timer;
    raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        // Small safety margin for amCharts' final layout/label pass.
        timer = setTimeout(() => {
          if (typeof window !== 'undefined') {
            window.__circumplexReady = true;
          }
        }, 200);
      });
    });
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      clearTimeout(timer);
    };
  }, []);

  const isTrend = reportType === 'trend';

  // Trend report: landscape page with T1 (previous, from trendContent) and
  // T2 (latest, from content) circumplexes side by side.
  if (isTrend) {
    const t2Content = data.content[0];
    const t1Content = (data.trendContent && data.trendContent[0]) || t2Content;

    return (
      <PageWrapper pageNumber={pageNumber} personName={personName} landscape>
        <SummaryIntro />

        <div className="pdf-circumplex-trend">
          <div className="pdf-circumplex-trend__col">
            <EditableText
              as="h3"
              className="pdf-circumplex-trend__label"
              path="circumplexData.t1Label"
              variant="inline"
              stripInlineWrap
              fallback="T1 (Previous 360 Feedback)"
            />
            <div className="pdf-circumplex-trend__frame">
              <Circumplex chartContent={t1Content} chartFontSizes={chartFontSizes} idKey="t1" />
            </div>
          </div>
          <div className="pdf-circumplex-trend__col">
            <EditableText
              as="h3"
              className="pdf-circumplex-trend__label"
              path="circumplexData.t2Label"
              variant="inline"
              stripInlineWrap
              fallback="T2 (Latest 360 Feedback)"
            />
            <div className="pdf-circumplex-trend__frame">
              <Circumplex chartContent={t2Content} chartFontSizes={chartFontSizes} idKey="t2" />
            </div>
          </div>
        </div>

        <ChartLegend />
      </PageWrapper>
    );
  }

  // Standard report: single circumplex, portrait.
  const chartContent = data.content[0];
  return (
    <PageWrapper pageNumber={pageNumber} personName={personName}>
      <SummaryIntro />
      <Circumplex chartContent={chartContent} chartFontSizes={chartFontSizes} />
      <ChartLegend />
    </PageWrapper>
  );
}
